import { useState, useEffect } from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

import { Select } from '../../common/components/select'
import { ExplicitChangeTextInput } from '../../common/components/explicit-change-text-input'
import { Switch, SWITCH_TYPE } from '../../common/components/switch'
import { NotificationManager } from '../../common/components/Notifications'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars } from '@fortawesome/pro-light-svg-icons'

import { useStyles } from './category-list-edit.style'

export const CategoryListEdit = function ({ children, size, categories = [], categoryLabel, isSingleSelection = false, listLabel, hideCategoryDropdown = false, hideLabelToggles = false, onChange = () => {} }) {
  const classes = useStyles()

  const [selectedCategory, setSelectedCategory] = useState(getDefaultCategory())

  useEffect(() => {
    // if category list changes, grab the updated selected category
    if (!selectedCategory) {
      setSelectedCategory(getDefaultCategory())
    } else {
      const selected = categories.find((s) => s.value === selectedCategory.value)
      setSelectedCategory(selected || getDefaultCategory())
    }
  }, [categories])

  function getCategoriesWithValues () {
    return categories.filter(({ values = [] }) => values.length > 0)
  }

  function getDefaultCategory () {
    const categoriesWithValues = getCategoriesWithValues()
    return categoriesWithValues.length > 0 && categoriesWithValues[0]
  }

  function handleTextInputChange (category, value, label) {
    const updated = categories.map((s) => {
      if (s.value === category) {
        s = { ...s }
        s.values = s.values.map((v) => {
          if (v.value === value) {
            v = { ...v, label }
          }
          return v
        })
      }
      return s
    })
    onChange(updated)
  }

  function handleDragEnd (draggable) {
    const { source, destination } = draggable

    if (!destination) {
      return
    }

    let updatedSelectedCategory
    const updated = categories.map((s) => {
      if (s.value === selectedCategory.value) {
        // clone & swap category values
        s = { ...s, values: [ ...s.values ] }
        const [removedItem] = s.values.splice(source.index, 1)
        s.values.splice(destination.index, 0, removedItem)
        updatedSelectedCategory = s
      }
      return s
    })

    onChange(updated)

    // Prevent flicker when rerenders prior to the useEffect due to category change
    if (updatedSelectedCategory) {
      setSelectedCategory(updatedSelectedCategory)
    }
  }

  function getKey (value, index) {
    if (value === null || value === undefined || value === '') {
      return `empty-${index}`
    }
    return value.toString()
  }

  function handleLabelToggle (categoryValue) {
    // Prevent all options from being hidden
    const visibleOptions = selectedCategory.values.filter(({ hide }) => (!hide))
    if ((visibleOptions.length === 1) && (visibleOptions[0].value === categoryValue.value)) {
      NotificationManager.create({ type: 'warning', timeOut: 10000, message: 'Oops! At least one label must be enabled.' })
      return
    }

    const updated = categories.map((s) => {
      if (s.value === selectedCategory.value) {
        s = { ...s, values: [ ...s.values ] }
        const index = s.values.findIndex(({ value }) => (value === categoryValue.value))
        if (index !== -1) {
          s.values[index].hide = !s.values[index].hide
        }
      }
      return s
    })

    onChange(updated)
  }

  function getDropdownValueSelection () {
    const currentValue = selectedCategory.values.find(({ hide }) => !hide)
    return currentValue || selectedCategory[0]
  }

  function selectDropdownValue (value) {
    selectedCategory.values.forEach((categoryValue) => {
      categoryValue.hide = value !== categoryValue
    })
    onChange(categories)
  }

  if (!selectedCategory) {
    return null
  }

  return (
    <div className={classes.container}>
      {!hideCategoryDropdown && (
        <div data-qa="category-field">
          <Select
            className={classes.select}
            label={categoryLabel}
            options={getCategoriesWithValues()}
            size={size}
            value={selectedCategory}
            onChange={(category) => {
              setSelectedCategory(category)
            }}
          />
        </div>
      )}
      {isSingleSelection
        ? <div data-qa="CategoryListEdit-value-select">
          <Select
            className={classes.select}
            label={listLabel}
            options={selectedCategory.values}
            size={size}
            value={getDropdownValueSelection(selectedCategory)}
            onChange={(value) => {
              selectDropdownValue(value)
            }}
          />
        </div>
        : <DragDropContext onDragEnd={handleDragEnd}>
          <div data-qa="CategoryListEdit-value-multiselect">
            {listLabel && <label className={classes.formLabel}>{listLabel}</label>}
            <Droppable droppableId="category-values">
              {(provided) => (
                <div className={classes.labels} ref={provided.innerRef} {...provided.droppableProps}>
                  {selectedCategory.values.map((categoryValue, index) => (
                    <Draggable key={getKey(categoryValue.value, index)} draggableId={getKey(categoryValue.value, index)} index={index} isDragDisabled={!!selectedCategory.disableReorder}>
                      {(provided) => (
                        <div className={classes.labelEdit} ref={provided.innerRef} {...provided.draggableProps}>
                          { selectedCategory.disableReorder ? '' : <div {...provided.dragHandleProps}><FontAwesomeIcon className={classes.grabber} tabIndex={-1} icon={faBars}/></div> }
                          <div className={classes.field} data-qa={`category-list-field-${categoryValue.value}`}>
                            <ExplicitChangeTextInput
                              size={size}
                              value={categoryValue.label || ''}
                              disabled={categoryValue.isDisabled || categoryValue.hide}
                              onChange={(event) => {
                                handleTextInputChange(selectedCategory.value, categoryValue.value, event.target.value)
                              }}
                            />
                          </div>
                          {!hideLabelToggles &&
                          <div data-qa={`switch-value-${index}`} className={classes.categoryToggleSwitch}>
                            <Switch
                              type={SWITCH_TYPE.MINI}
                              isOn={!categoryValue.hide}
                              onToggle={() => { handleLabelToggle(categoryValue) }}
                              tabIndex="-1"
                              className={classes.hide}
                            />
                          </div>
                          }
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>
      }
      {children && (
        <div className={classes.additionalForm}>
          {typeof children === 'function' ? children({ classes }) : children}
        </div>
      )}
    </div>
  )
}
